import { useEffect, useRef, useState } from "react";
import styles from "./Dropdown.module.css";

export interface DropdownOption {
	value: string;
	label: string;
	flag?: string;
	disabled?: boolean;
}

interface DropdownProps {
	options: DropdownOption[];
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	variant?: "default" | "onboarding";
	error?: boolean;
	className?: string;
}

export function Dropdown({
	options,
	value,
	onChange,
	placeholder = "Select an option",
	variant = "default",
	error = false,
	className = "",
}: DropdownProps) {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const selectedOption = options.find((option) => option.value === value);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const handleOptionClick = (option: DropdownOption) => {
		if (option.disabled) return;
		onChange(option.value);
		setIsOpen(false);
	};

	return (
		<div ref={dropdownRef} className={`${styles.container} ${className}`}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={`${styles.trigger} ${styles[variant]} ${isOpen ? styles.triggerOpen : ""} ${error ? styles.error : ""}`}
			>
				<span
					className={
						selectedOption ? styles.triggerTextSelected : styles.triggerText
					}
				>
					{selectedOption ? (
						<>
							{selectedOption.flag && (
								<span className={styles.flag}>{selectedOption.flag}</span>
							)}
							{selectedOption.label}
						</>
					) : (
						placeholder
					)}
				</span>
				<span className={`${styles.arrow} ${isOpen ? styles.arrowUp : ""}`}>
					▼
				</span>
			</button>

			{isOpen && (
				<div
					className={`${styles.dropdown} ${styles[`dropdown${variant.charAt(0).toUpperCase() + variant.slice(1)}`]}`}
				>
					{options.map((option) => (
						<button
							key={option.value}
							type="button"
							onClick={() => handleOptionClick(option)}
							disabled={option.disabled}
							className={`${styles.option} ${value === option.value ? styles.optionSelected : ""} ${option.disabled ? styles.optionDisabled : ""}`}
						>
							{option.flag && (
								<span className={styles.flag}>{option.flag}</span>
							)}
							<span>{option.label}</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
